var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose } from 'class-transformer';
import { IsNotEmpty, IsString, IsUUID, Matches, MaxLength } from 'class-validator';
import { VersandOperatorActKind } from '../enums/versand-operator-act-kind.enum.js';
/**
 * How long the reason for an operator action may be (#1630).
 *
 * ⚠ **One number for input validation and column.** If it stood in two places,
 * the input would accept what the column truncates — and a truncated reason is
 * worse in a log than none: it looks like one.
 */
export const VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH = 1000;
/**
 * An **unmatched Rückmeldung** in the Versand operator's queue
 * (TM-9636 / #1630, #1594).
 *
 * ⚠ **Four pieces of information, and none of them from the body.** What lies
 * here may be a Verarbeitungsmeldung that could not be matched to an attempt —
 * and every Kasse settles its content itself; #1594 counts it explicitly as
 * payload. The operator therefore sees **from whom**, **about what**, **when**
 * and **why it lies here**, and decides from that. There is no path to the body
 * in this wave; payload access is TM-9637.
 *
 * ⚠ **The subject is not a body.** Under A7 §3.2.2.1 it carries both IKs and
 * the Transfernummer and is precisely what a human recognises the mail's
 * destination by. It stands here raw, because an interpreted version would be
 * exactly the reading the matching failed on.
 */
export class VersandUnmatchedRueckmeldungDto {
    id;
    /** The mail's `Message-ID`, or `sha256:…` from its bytes. */
    rueckmeldungMessageId;
    /** The `From` header, raw and uninterpreted. */
    sender;
    /** The subject, as far as there was one. */
    subject;
    /**
     * The IK the subject names as the recipient, or `null`.
     *
     * ⚠ **`null` means: the mail carried no Rückmeldung subject** and therefore
     * cannot be matched to any Versandversuch — only discarded. The view says so
     * instead of offering a match button that always refuses.
     */
    recipientIk;
    /** ISO-8601. */
    receivedAt;
    reason;
    /**
     * The mail's head, up to the first blank line — `Received` chain, `Date`,
     * `Content-Type`, the subject before decoding.
     *
     * ⚠ **The head and never the body.** It is what tells *why* a mail landed here
     * when the subject alone does not, and it is cut off in the backend already.
     * Empty when no blank line ends the head: then head and body cannot be told apart.
     */
    rawHeaders;
}
/**
 * A **Versandversuch this Rückmeldung can be matched to** (#1630).
 *
 * ⚠ **Only attempts of the same recipient.** What the subject names as the
 * recipient narrows the choice before the operator sees it — a list across all
 * practices would be an invitation to hang a Dateiablehnung on the Lieferung of
 * a practice that does not have it.
 *
 * ⚠ **Metadata, as everywhere in the console** (#1594): which attempt is meant
 * is answered by DAR, Transfernummer and file name.
 *
 * ⚠ **No IK in the row.** All candidates share the recipient — the list is
 * selected by it — and under Versand the Absender too: Therame's IK stands on
 * every one. Three columns saying the same thing in every row do not help
 * telling them apart; the mail's IK stands on the queue row above, and what
 * hangs on a Lieferung the detail column shows.
 */
export class VersandRueckmeldungCandidateDto {
    lieferungAttemptId;
    lieferungId;
    /** The Standort's name — in this data model the practice's name. */
    officeLocationName;
    datenaustauschreferenz;
    /** Spec name: "Transfernummer". */
    transferNumber;
    filenameStem;
    /** When the mail server accepted this attempt's mail. ISO 8601. */
    submittedAt;
}
/**
 * A **Versand operator's action** as the practice and the console read it
 * (TM-9636 / #1630).
 *
 * ⚠ **With a name, and that is the decision.** A Lieferung that is *accepted*
 * although no Rückmeldung could ever be matched to it has an author; without
 * them the practice would be left with the claim alone. The name is a snapshot
 * from the moment of the action.
 *
 * ⚠ **Without a reference to the discarded mail.** What was discarded belonged
 * to no Lieferung; an id for it in a practice response would be a pointer to
 * post that concerns another practice.
 *
 * ⚠ **`@Expose()`, because the same class hangs in the practice's progress
 * view.** That response goes through `class-transformer` with
 * `excludeExtraneousValues`; without the marker an empty object would arrive
 * there per action. The key set stands in `gkv-lieferung-metadaten.spec.ts` and
 * makes every further field a decision.
 */
export class VersandOperatorActDto {
    id;
    /** ISO 8601. */
    at;
    kind;
    /** The name as it stood on the account at the moment of the action. */
    operatorName;
    reason;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], VersandOperatorActDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], VersandOperatorActDto.prototype, "at", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], VersandOperatorActDto.prototype, "kind", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], VersandOperatorActDto.prototype, "operatorName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], VersandOperatorActDto.prototype, "reason", void 0);
export class VersandPromotionFailureDto {
    /** The id of the Versandversuch or of the Übergabe file. */
    id;
    /** `VERSAND` for a Versandversuch, `UEBERGABE` for a run file. */
    route;
    /** The Standort's name — in this data model the practice's name. */
    officeLocationName;
    gkvBillingRunId;
    filenameStem;
    /** What the promotion step reported. */
    errorMessage;
    /** When the file was produced or the attempt sent. ISO 8601. */
    at;
}
/**
 * Body of `POST /v1/versand/rueckmeldungen/:id/assign` (#1630).
 *
 * ⚠ **The reason is mandatory.** The match moves a Lieferung although the
 * machine could not match it; without the sentence saying why a human
 * nevertheless held it to be right, the practice view would show a state change
 * without an occasion.
 */
export class AssignUnmatchedRueckmeldungDto {
    lieferungAttemptId;
    reason;
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], AssignUnmatchedRueckmeldungDto.prototype, "lieferungAttemptId", void 0);
__decorate([
    IsString(),
    IsNotEmpty()
    // ⚠ **`@Matches` beside `@IsNotEmpty`, and that is not belt and braces.**
    // `@IsNotEmpty` only refuses the empty string; a reason made of spaces would
    // get through, and the practice view would show a state change with an empty
    // line underneath.
    ,
    Matches(/\S/, { message: 'The reason must not be whitespace only.' }),
    MaxLength(VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH),
    __metadata("design:type", String)
], AssignUnmatchedRueckmeldungDto.prototype, "reason", void 0);
/**
 * Body of `POST /v1/versand/rueckmeldungen/:id/discard` (#1630).
 *
 * ⚠ **Mandatory here too.** *Discarded* is the statement that a mail belongs to
 * no Lieferung; making it without a reason would mean making it uncheckably.
 */
export class DiscardUnmatchedRueckmeldungDto {
    reason;
}
__decorate([
    IsString(),
    IsNotEmpty(),
    Matches(/\S/, { message: 'The reason must not be whitespace only.' }),
    MaxLength(VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH),
    __metadata("design:type", String)
], DiscardUnmatchedRueckmeldungDto.prototype, "reason", void 0);
/**
 * Body of `POST /v1/versand/lieferungen/:id/send` (#1958): a Versand-Operator
 * sends one waiting Prüf-Lieferung.
 *
 * ⚠ **The reason is mandatory, as on every operator act.** The practice reads it
 * with the operator's name on its Lieferung; a Lieferung that left by hand
 * without one would be a send nobody can account for.
 *
 * ⚠ **In this file and not beside the Lieferung DTOs**, so that the one limit
 * {@link VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH} stands next to every body that
 * reads it.
 */
export class SendTestFileLieferungDto {
    reason;
}
__decorate([
    IsString(),
    IsNotEmpty(),
    Matches(/\S/, { message: 'The reason must not be whitespace only.' }),
    MaxLength(VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH),
    __metadata("design:type", String)
], SendTestFileLieferungDto.prototype, "reason", void 0);
/**
 * Body of `POST /v1/versand/lieferungen/:id/test-file` (#1959).
 *
 * ⚠ **Mandatory like on every operator act.** The office reads the reason on its
 * Lieferung beside the moment its file was taken — „TA-Validator", typically.
 */
export class DownloadTestFileDto {
    reason;
}
__decorate([
    IsString(),
    IsNotEmpty(),
    Matches(/\S/, { message: 'The reason must not be whitespace only.' }),
    MaxLength(VERSAND_OPERATOR_ACT_REASON_MAX_LENGTH),
    __metadata("design:type", String)
], DownloadTestFileDto.prototype, "reason", void 0);
//# sourceMappingURL=versand-rueckmeldung-queue.dto.js.map